import React from 'react'
import { useAppDispatch, useAppSelector } from '../../../hooks/redux'
import { getConfig, updateConfigNamespace } from '../../../lib/api'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { fetchTimezones } from '@SonarCX/slices/timezone'

import SettingsSaveButton from '@SonarCX/components/atoms/SettingsSaveButton'
import SettingsPage from '../SettingsPage'
import DefaultsSettings from './DefaultsSettings'
import EnvironmentSettings from './EnvironmentSettings'
import NoPermissionContainer from '../../../components/molecules/NoPermissionContainer'

import { AddErrorAlert, AddSuccessAlert } from '../../../slices/alert'

const SYSTEM_CONFIG_SYS_KEY = 'system'
const USERS_CONFIG_SYS_KEY = 'users'
export const ENV_NAME_KEY = 's_environment_name'
export const ENV_TZ_KEY = 's_environment_timezone'
export const EXTERNAL_SETTING_KEY = 'u_default_external_type_id'
export const INTERNAL_SETTING_KEY = 'u_default_internal_type_id'

const stripValuesFromArray = (obj, arr) => {
  const keys = Object.keys(obj)
  //remove existing system configs prior adding updated ones
  return arr.filter(i => !keys.some(j => j === i.key))
}

const GeneralSettings = () => {
  const dispatch = useAppDispatch()
  const {
    loaded: timezonesLoaded,
    current: userTz,
    loading: timezonesLoading,
    error: tzError
  } = useAppSelector(storeState => storeState.timezone)
  const {
    loaded: envLoaded,
    env,
    reload: reloadEnv,
    error: envError
  } = useEnvironment()
  const [configLoaded, setConfigLoaded] = React.useState(false)
  const [config, setConfig] = React.useState(null)
  const [edited, setEdited] = React.useState({ system: false, users: false })
  const [error, setError] = React.useState(null)
  const [saving, setSaving] = React.useState(false)
  const [envNameError, setEnvNameError] = React.useState(null)

  const [environmentValues, setEnvironmentValues] = React.useState({
    [ENV_NAME_KEY]: env?.name,
    [ENV_TZ_KEY]:
      config?.system?.find(t => t.key === ENV_TZ_KEY)?.value || userTz
  })
  const [defaultValues, setDefaultValues] = React.useState({
    [EXTERNAL_SETTING_KEY]:
      config?.users?.find(t => t.key === EXTERNAL_SETTING_KEY)?.value ||
      'system_default',
    [INTERNAL_SETTING_KEY]:
      config?.users?.find(t => t.key === INTERNAL_SETTING_KEY)?.value ||
      'system_default'
  })

  React.useEffect(() => {
    if (configLoaded) {
      return
    }
    const loadConfig = async () => {
      try {
        const data = await getConfig()
        setConfig(data)
      } catch (err) {
        setError(err)
      } finally {
        setConfigLoaded(true)
      }
    }
    setConfig(null)
    loadConfig()
  }, [configLoaded])

  React.useEffect(() => {
    if (!timezonesLoaded && !timezonesLoading) {
      dispatch(fetchTimezones())
    }
  }, [timezonesLoaded, timezonesLoading, dispatch])

  //https://app.clickup.com/t/36612886/ES-1073
  //When loading the environment name, always use the one in the /environments setting.
  //When the save has been completed, you can update the environment name in memory with the one returned from the config endpoint.
  React.useEffect(() => {
    if (envLoaded && environmentValues.s_environment_name === undefined) {
      setEnvironmentValues({
        ...environmentValues,
        s_environment_name: env.name
      })
    }
  }, [envLoaded, env, environmentValues])

  React.useEffect(() => {
    if (configLoaded && config?.users) {
      setDefaultValues({
        [EXTERNAL_SETTING_KEY]:
          config?.users?.find(t => t.key === EXTERNAL_SETTING_KEY)?.value ||
          'system_default',
        [INTERNAL_SETTING_KEY]:
          config?.users?.find(t => t.key === INTERNAL_SETTING_KEY)?.value ||
          'system_default'
      })
    }
    if (configLoaded && config?.system) {
      setEnvironmentValues(old => ({
        ...old,
        [ENV_TZ_KEY]:
          config?.system?.find(t => t.key === ENV_TZ_KEY)?.value || userTz
      }))
    }
  }, [configLoaded, config, userTz])

  const handleChange = React.useCallback((key, value, isSystem) => {
    if (isSystem) {
      setEdited(old => ({ ...old, system: true }))
      setEnvironmentValues(old => ({ ...old, [key]: value }))
    } else {
      setEdited(old => ({ ...old, users: true }))
      setDefaultValues(old => ({ ...old, [key]: value }))
    }
  }, [])

  const saveEnvironmentSettings = React.useCallback(async () => {
    try {
      const updatedSettings = stripValuesFromArray(
        environmentValues,
        config?.system
      )
      for (const [key, value] of Object.entries(environmentValues)) {
        updatedSettings.push({ key, value: value.trim() })
      }

      await updateConfigNamespace(SYSTEM_CONFIG_SYS_KEY, {
        items: updatedSettings
      })
      reloadEnv()
      return updatedSettings
    } catch (err) {
      console.error('Error saving environments', err)
    }
  }, [config, environmentValues, reloadEnv])

  const saveDefaultSettings = React.useCallback(async () => {
    try {
      const updatedSettings = stripValuesFromArray(defaultValues, config?.users)
      for (const [key, value] of Object.entries(defaultValues)) {
        updatedSettings.push({
          key,
          value: value === 'system_default' ? null : value
        })
      }
      await updateConfigNamespace(USERS_CONFIG_SYS_KEY, {
        items: updatedSettings
      })
      return updatedSettings
    } catch (err) {
      console.error('Error saving default settings', err)
    }
  }, [config, defaultValues])

  const handleSaveClick = React.useCallback(async () => {
    try {
      setSaving(true)

      let users,
        system = []
      if (edited.users) {
        users = await saveDefaultSettings()
      }
      if (edited.system) {
        system = await saveEnvironmentSettings()
      }

      //update all config in one call
      if (edited.users && edited.system) {
        setConfig(old => ({ ...old, users, system }))
      } else if (edited.users) {
        setConfig(old => ({ ...old, users }))
      } else if (edited.system) {
        setConfig(old => ({ ...old, system }))
      }
      dispatch(AddSuccessAlert('Changes saved'))
      setEdited({ system: false, users: false })
      setSaving(false)
    } catch (err) {
      dispatch(AddErrorAlert({ err }))
      setSaving(false)
    }
  }, [
    edited,
    saveDefaultSettings,
    saveEnvironmentSettings,
    setConfig,
    dispatch
  ])

  if (!configLoaded) return null

  return (
    <NoPermissionContainer skipAgentPage error={tzError || envError || error}>
      <SettingsPage title='Environment'>
        <EnvironmentSettings
          config={config}
          envNameError={envNameError}
          setEnvNameError={setEnvNameError}
          values={environmentValues}
          handleChange={handleChange}
          configLoaded={configLoaded}
          timezonesLoaded={timezonesLoaded}
        />

        <DefaultsSettings
          values={defaultValues}
          handleChange={handleChange}
          configLoaded={configLoaded}
        />

        <SettingsSaveButton
          onClick={handleSaveClick}
          loading={saving}
          disabled={Boolean(envNameError)}
        />
      </SettingsPage>
    </NoPermissionContainer>
  )
}

export default GeneralSettings
